import { clubPath, clubUrl } from '../clubhouse';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, AlertTriangle, Snowflake, Sun, Wind, CloudRain, Shield, Thermometer, Check, ChevronRight } from 'lucide-react';
import { alex } from '../services/api';
import { toast } from 'sonner';
import usePushNotifications from '../hooks/usePushNotifications';
import PushActivationBanner from './PushActivationBanner';

const SEVERITY_STYLES = {
  critique: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-500', text: 'text-red-700', dot: 'bg-red-500' },
  haute: { bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-500', text: 'text-orange-700', dot: 'bg-orange-500' },
  moyenne: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-500', text: 'text-amber-700', dot: 'bg-amber-500' },
  basse: { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-500', text: 'text-blue-700', dot: 'bg-blue-500' },
};

const ICON_MAP = {
  snowflake: Snowflake,
  sun: Sun,
  wind: Wind,
  'cloud-rain': CloudRain,
  shield: Shield,
  thermometer: Thermometer,
  alert: AlertTriangle,
};

const WeatherAlertBell = ({ userId }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const prevCountRef = useRef(0);
  const { isSubscribed: pushSubscribed, subscribe: pushSubscribe, loading: pushLoading } = usePushNotifications(userId, 'admin');

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await alex.getWeatherAlertsUnreadCount();
      const newCount = response.data?.count || 0;
      
      // Toast si nouvelles alertes détectées
      if (newCount > prevCountRef.current && prevCountRef.current >= 0) {
        const diff = newCount - prevCountRef.current;
        if (diff > 0 && prevCountRef.current > 0) {
          toast.warning(`${diff} nouvelle${diff > 1 ? 's' : ''} alerte${diff > 1 ? 's' : ''} météo`, {
            description: 'Cliquez sur la cloche pour voir les détails',
            duration: 8000,
          });
        }
      }
      prevCountRef.current = newCount;
      setUnreadCount(newCount);
    } catch (error) {
      // Silently fail
    }
  }, []);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await alex.getWeatherAlerts();
      setAlerts(response.data?.alerts || []);
    } catch (error) {
      console.error('Erreur chargement alertes:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Polling toutes les 5 minutes
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Charger les alertes et auto-mark-read quand on ouvre le panel
  useEffect(() => {
    if (isOpen) {
      fetchAlerts();
      // Auto-mark all read à l'ouverture
      if (unreadCount > 0) {
        setUnreadCount(0);
        alex.markAllAlertsRead().catch(() => {});
        setAlerts(prev => prev.map(a => ({ ...a, read: true })));
      }
    }
  }, [isOpen, fetchAlerts]);

  // Fermer le panel au clic extérieur
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside, { passive: true });
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  const handleMarkAllRead = async () => {
    try {
      await alex.markAllAlertsRead();
      setUnreadCount(0);
      setAlerts(prev => prev.map(a => ({ ...a, read: true })));
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleDismiss = async (alertId, e) => {
    e.stopPropagation();
    try {
      await alex.dismissAlert(alertId);
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      if (!alerts.find(a => a.id === alertId)?.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleAlertClick = async (alert) => {
    if (!alert.read) {
      try {
        await alex.markAlertRead(alert.id);
        setAlerts(prev => prev.map(a => a.id === alert.id ? { ...a, read: true } : a));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Erreur:', error);
      }
    }
  };

  const getAlertIcon = (iconName) => {
    const IconComponent = ICON_MAP[iconName] || AlertTriangle;
    return IconComponent;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `Il y a ${diffMins}min`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="relative" ref={panelRef} data-testid="weather-alert-bell">
      {/* Cloche */}
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className="relative p-2 rounded-lg hover:bg-neutral-100 transition-colors"
        data-testid="alert-bell-button"
      >
        <Bell size={20} className={unreadCount > 0 ? 'text-orange-600' : 'text-neutral-500'} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 animate-pulse" data-testid="alert-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
        {!pushSubscribed && !pushLoading && unreadCount === 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-neutral-200 z-50 overflow-hidden" data-testid="alert-panel">
          {/* Push activation banner */}
          {!pushSubscribed && <PushActivationBanner pushSubscribe={pushSubscribe} />}

          {/* Header */}
          <div className="px-4 py-3 border-b border-neutral-100 flex items-center justify-between bg-neutral-50">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-orange-500" />
              <span className="font-semibold text-sm text-neutral-900">Alertes Météo</span>
            </div>
          </div>

          {/* Alerts list */}
          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8 text-neutral-400">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
              </div>
            ) : alerts.length === 0 ? (
              <div className="py-8 text-center text-neutral-400">
                <Sun size={28} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm font-medium">Aucune alerte active</p>
                <p className="text-xs mt-1">Les conditions météo sont favorables</p>
              </div>
            ) : (
              alerts.map((alert) => {
                const styles = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.moyenne;
                const IconComp = getAlertIcon(alert.icon);
                return (
                  <div
                    key={alert.id}
                    onClick={() => handleAlertClick(alert)}
                    className={`px-4 py-3 border-b border-neutral-50 cursor-pointer transition-colors hover:bg-neutral-50 ${!alert.read ? 'bg-orange-50/30' : ''}`}
                    data-testid={`alert-item-${alert.id}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon + severity indicator */}
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${styles.bg}`}>
                        <IconComp size={16} className={styles.text} />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {!alert.read && <div className={`w-2 h-2 rounded-full flex-shrink-0 ${styles.dot}`} />}
                          <span className={`text-xs font-bold uppercase px-1.5 py-0.5 rounded ${styles.bg} ${styles.text}`}>
                            {alert.severity}
                          </span>
                          <span className="text-[10px] text-neutral-400 ml-auto flex-shrink-0">{formatTime(alert.created_at)}</span>
                        </div>
                        <p className="text-sm font-semibold text-neutral-900 mt-1">{alert.title}</p>
                        <p className="text-xs text-neutral-600 mt-0.5">{alert.detail}</p>
                        <p className="text-xs text-neutral-500 mt-1 leading-relaxed">{alert.message}</p>
                      </div>

                      {/* Dismiss */}
                      <button
                        onClick={(e) => handleDismiss(alert.id, e)}
                        className="p-1 hover:bg-neutral-200 rounded-lg text-neutral-400 hover:text-neutral-600 transition-colors flex-shrink-0"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {alerts.length > 0 && (
            <div className="px-4 py-2 border-t border-neutral-100 bg-neutral-50">
              <a href={clubPath('/alex')} className="text-xs text-primary font-medium flex items-center gap-1 hover:underline">
                Voir dans BASS IA <ChevronRight size={12} />
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WeatherAlertBell;
